import React, { useCallback, memo } from 'react';
import { Box, BoxProps, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlycePalette, Icon } from '@alycecom/ui';
import classNames from 'classnames';

import { IAlyceCustomTheme } from '../../styles/alyceTheme';

const useStyles = makeStyles<IAlyceCustomTheme, Partial<IInformationCardProps>>(({ palette, spacing }) => ({
  root: props => ({
    border: `2px solid ${palette[props.borderColor as keyof AlycePalette] || props.borderColor}`,
    borderRadius: 6,
  }),
  title: {
    textAlign: 'left',
    marginBottom: spacing(1),
    color: palette.text.primary,
  },
  action: ({ isLoading }) => ({
    bottom: 0,
    right: 16,
    fontFamily: 'Lato',
    lineHeight: 1.33,
    color: isLoading ? palette.text.disabled : palette.customStyling.highlightColor,
    cursor: isLoading ? 'arrow' : 'pointer',
    fontWeight: 'bold',
  }),
  icon: {
    marginRight: 4,
  },
}));

export interface IInformationCardProps extends BoxProps {
  title?: string;
  subTitle?: React.ReactNode;
  isLoading?: boolean;
  borderColor?: string;
  actionText?: string;
  onAction?: () => void;
  children?: React.ReactNode;
}

const InformationCard = ({
  title,
  subTitle,
  isLoading = false,
  borderColor = 'secondary.main',
  actionText = 'Edit',
  onAction,
  children,
  ...boxProps
}: IInformationCardProps) => {
  const classes = useStyles({ borderColor, isLoading });

  const handleAction = useCallback(() => {
    if (!isLoading && onAction) {
      onAction();
    }
  }, [isLoading, onAction]);

  return (
    <Box py={0} width={1} color="text.primary" textAlign="center">
      {title && <Typography className={classNames('H4-Dark-Bold', classes.title)}>{title}</Typography>}
      {subTitle && (
        <Box mt={1} mb={1} display="flex" alignItems="center" textAlign="left">
          {subTitle}
        </Box>
      )}
      <Paper elevation={0} classes={{ root: classes.root }}>
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight={[120, 160]}
          p={3}
          {...boxProps}
        >
          {children}
          {onAction && (
            <Box
              data-testid="InformationCard-ActionButton"
              className={classes.action}
              position="absolute"
              display="inline-block"
              fontSize={12}
              onClick={handleAction}
              pb={3}
            >
              <Icon icon="pencil" size="sm" className={classes.icon} />
              {actionText}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default memo(InformationCard);
